/* AO Home V3 — homepage Add to cart (final pass, group C2).
   Contract: finalpass-20260915/HOME-FLOW.md §2.
   Each collection card renders <form data-ao-home-add-form action="/cart/add"> whose hidden inputs are the cart
   lines (the Complete Ritual form carries exactly the Oil PDP Complete Hair Ritual lines). This controller posts
   that FormData to /cart/add.js with bundled section rendering, renders the existing Shrine cart drawer,
   confirms per-variant quantity deltas in /cart.js, refreshes the header count through aocdx:cart-updated
   (aof-shared.js / Cart V4) and opens the drawer. No parallel cart, no product schema, no main product form.
   Listeners are delegated from document and registered once (guard below), so Theme Editor section
   reloads never bind twice; label timers are cleared on section unload. */
(() => {
  'use strict';

  if (window.AoHomeV3Commerce) return;
  const api = { version: 1 };
  window.AoHomeV3Commerce = api;

  const ROOT = '[data-ao-home-commerce]';
  const FORM = 'form[data-ao-home-add-form]';
  const SUBMIT = '[data-ao-home-add-submit]';
  const PROXY = '[data-ao-home-add-proxy]';
  const KINDS = /^(oil|protocol|pairing)$/;
  const CONFIRM_MS = 10000;
  const POLL_MS = 250;
  const ADDED_MS = 2000;

  const labelTimers = new Map();
  let busy = false;

  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const routes = () => window.routes || {};
  const cartJsUrl = () => `${routes().cart_url || '/cart'}.js`;
  const addJsUrl = () => `${routes().cart_add_url || '/cart/add'}.js`;

  const copyFor = (root) => {
    const data = (root && root.dataset) || {};
    return {
      adding: data.statusAdding || 'Adding to your cart…',
      added: data.statusAdded || 'Added to your cart.',
      error: data.statusError || 'The cart did not update. Please try again.',
      review: data.statusReview || 'We could not confirm the cart update. Opening your cart so you can review it.',
      addedLabel: data.addedLabel || 'Added'
    };
  };

  const fetchCart = async (timeout = 3000) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(cartJsUrl(), {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (_error) {
      return null;
    } finally {
      window.clearTimeout(timer);
    }
  };

  /* Expected quantity per variant, read from the form's own items[n][id] / items[n][quantity] inputs. */
  const expectedLines = (form) => {
    const ids = {};
    const quantities = {};
    new FormData(form).forEach((value, name) => {
      const match = /^items\[(\d+)\]\[(id|quantity)\]$/.exec(name);
      if (!match) return;
      if (match[2] === 'id') ids[match[1]] = String(value).trim();
      else quantities[match[1]] = Number(value) || 0;
    });
    const expected = new Map();
    Object.keys(ids).forEach((index) => {
      const id = ids[index];
      if (!/^\d+$/.test(id) || id === '0') return;
      expected.set(id, (expected.get(id) || 0) + (quantities[index] || 1));
    });
    return expected;
  };

  const variantQuantity = (cart, variantId) => (cart && Array.isArray(cart.items) ? cart.items : [])
    .filter((item) => String(item.variant_id) === variantId)
    .reduce((total, item) => total + (Number(item.quantity) || 0), 0);

  /* Per-variant deltas only: Cart V4 may reconcile the shipping-protection line and change item_count. */
  const deltasMatch = (before, after, expected) => {
    if (!before || !after || !expected.size) return false;
    for (const [variantId, quantity] of expected) {
      if (variantQuantity(after, variantId) - variantQuantity(before, variantId) !== quantity) return false;
    }
    return true;
  };

  const drawerNode = () => document.querySelector('cart-drawer');

  const sectionIds = () => {
    const drawer = drawerNode();
    try {
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        const ids = drawer.getSectionsToRender()
          .map((entry) => entry && (entry.id || entry.section))
          .filter(Boolean);
        if (ids.length) return [...new Set(ids)];
      }
    } catch (_error) {
      // Stable Shopify section ids used by Shrine ProductForm on the product pages.
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  };

  const openDrawer = (trigger) => {
    const drawer = drawerNode();
    if (!drawer || typeof drawer.open !== 'function') return false;
    if (!drawer.classList.contains('active')) drawer.open(trigger);
    return true;
  };

  const isAvailable = (button) => button.dataset.aoHomeAvailable !== 'false';

  const controls = () => [...document.querySelectorAll(`${ROOT} ${SUBMIT}, ${PROXY}`)];

  const setBusy = (state, trigger) => {
    busy = state;
    controls().forEach((button) => {
      if (state) {
        if (button === trigger) {
          // Keep focus on the pressed button: mark it busy instead of disabling it.
          button.setAttribute('aria-busy', 'true');
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.disabled = true;
        }
        return;
      }
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      button.disabled = !isAvailable(button);
    });
  };

  const statusNodes = (form, trigger) => {
    const proxyWrap = trigger && trigger.matches(PROXY) ? trigger.closest('[data-ao-home-proxy-wrap]') : null;
    const scope = proxyWrap || form;
    const node = scope ? scope.querySelector('[data-ao-home-status]') : null;
    return node ? [node] : [];
  };

  const showStatus = (nodes, message) => {
    nodes.forEach((node) => {
      node.textContent = message || '';
    });
  };

  const flashAdded = (button, label) => {
    const text = button ? button.querySelector('[data-ao-home-add-text]') : null;
    if (!text) return;
    if (!text.dataset.aoHomeLabel) text.dataset.aoHomeLabel = text.textContent;
    window.clearTimeout(labelTimers.get(button));
    text.textContent = label;
    labelTimers.set(button, window.setTimeout(() => {
      labelTimers.delete(button);
      if (text.isConnected) text.textContent = text.dataset.aoHomeLabel;
    }, ADDED_MS));
  };

  const addToCart = async (form, trigger) => {
    if (busy || !form || !form.isConnected) return;
    const root = form.closest(ROOT);
    const submit = form.querySelector(SUBMIT);
    if (!submit || !isAvailable(submit)) return;
    const button = trigger || submit;
    if (!isAvailable(button)) return;
    const expected = expectedLines(form);
    if (!expected.size) return;

    const copy = copyFor(root);
    const nodes = statusNodes(form, button);
    // Fix round 2 · HOME-FIX2: a new add clears any status left on the other cards (e.g. an earlier error).
    document.querySelectorAll(`${ROOT} [data-ao-home-status], [data-ao-home-proxy-wrap] [data-ao-home-status]`).forEach((node) => {
      if (!nodes.includes(node)) node.textContent = '';
    });
    setBusy(true, button);
    showStatus(nodes, copy.adding);

    try {
      const before = await fetchCart();
      const body = new FormData(form);
      body.append('sections', sectionIds().join(','));
      body.append('sections_url', window.location.pathname);

      let response;
      try {
        response = await fetch(addJsUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });
      } catch (_networkError) {
        throw new Error(copy.error);
      }

      let json = null;
      try {
        json = await response.json();
      } catch (_parseError) {
        json = null;
      }
      if (!response.ok || !json || json.status) {
        throw new Error((json && (json.description || json.message)) || copy.error);
      }

      const drawer = drawerNode();
      if (drawer && json.sections && typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents(json);
        } catch (_renderError) {
          // The count event and drawer open below still reflect the updated cart.
        }
      }

      let after = null;
      let confirmed = false;
      const deadline = performance.now() + CONFIRM_MS;
      while (performance.now() < deadline) {
        after = await fetchCart(Math.min(1500, Math.max(300, deadline - performance.now())));
        if (!before) {
          confirmed = Boolean(after);
          break;
        }
        if (deltasMatch(before, after, expected)) {
          confirmed = true;
          break;
        }
        await sleep(POLL_MS);
      }

      if (after) document.dispatchEvent(new CustomEvent('aocdx:cart-updated', { detail: { cart: after } }));
      showStatus(nodes, confirmed ? copy.added : copy.review);
      if (confirmed) flashAdded(button, copy.addedLabel);
      await sleep(120);
      openDrawer(button);
    } catch (error) {
      showStatus(nodes, (error && error.message) || copy.error);
      if (button.isConnected) button.focus({ preventScroll: true });
    } finally {
      setBusy(false);
    }
  };

  const formFor = (kind) => (KINDS.test(kind || '')
    ? document.querySelector(`${ROOT} ${FORM}[data-ao-home-add-form="${kind}"]`)
    : null);

  const syncProxies = () => {
    document.querySelectorAll(PROXY).forEach((proxy) => {
      const wrap = proxy.closest('[data-ao-home-proxy-wrap]') || proxy;
      const form = formFor(proxy.dataset.aoHomeAddProxy);
      const submit = form ? form.querySelector(SUBMIT) : null;
      if (!submit) {
        wrap.hidden = true;
        return;
      }
      if (submit.dataset.aoHomeAvailable === 'false') proxy.dataset.aoHomeAvailable = 'false';
      proxy.disabled = busy || !isAvailable(proxy);
      wrap.hidden = false;
    });
  };

  const onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM) || !form.closest(ROOT)) return;
    if (typeof window.fetch !== 'function' || typeof window.FormData !== 'function') return;
    event.preventDefault();
    const submitter = event.submitter && event.submitter.matches && event.submitter.matches(SUBMIT) ? event.submitter : null;
    addToCart(form, submitter);
  };

  const onClick = (event) => {
    const proxy = event.target && event.target.closest ? event.target.closest(PROXY) : null;
    if (!proxy) return;
    event.preventDefault();
    const form = formFor(proxy.dataset.aoHomeAddProxy);
    if (form) addToCart(form, proxy);
  };

  document.addEventListener('submit', onSubmit);
  document.addEventListener('click', onClick);

  document.addEventListener('shopify:section:load', () => {
    if (!busy) setBusy(false);
    syncProxies();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const scope = event.target;
    labelTimers.forEach((timer, button) => {
      if (!button.isConnected || (scope && scope.contains && scope.contains(button))) {
        window.clearTimeout(timer);
        labelTimers.delete(button);
      }
    });
    window.requestAnimationFrame(syncProxies);
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    busy = false;
    setBusy(false);
    document.querySelectorAll(`${ROOT} [data-ao-home-status], [data-ao-home-proxy-wrap] [data-ao-home-status]`).forEach((node) => {
      node.textContent = '';
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncProxies, { once: true });
  else syncProxies();

  api.add = addToCart;
  api.syncProxies = syncProxies;
})();
